import React from 'react';
import { Award, MessageSquare } from 'lucide-react';

interface Props {
  session: any;
}

const fmtBRL4 = (v: any) => {
  const n = typeof v === 'number' ? v : (v ? parseFloat(String(v)) : NaN);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

const SITUACAO_BADGES: Record<string, { label: string; color: string }> = {
  E: { label: 'Em negociação', color: 'bg-amber-500/15 text-amber-300' },
  N: { label: 'Negociada', color: 'bg-blue-500/15 text-blue-300' },
  D: { label: 'Desclassificada', color: 'bg-red-500/15 text-red-300' },
  H: { label: 'Habilitada', color: 'bg-emerald-500/15 text-emerald-300' },
};

// `situacao` (numeric, vem do portal) — distinto de `situacaoUltimaNegociacao` (E/N/D/H)
// 5 = Inabilitada (visto em Dispensa 88/2026 LNCC)
const SITUACAO_GLOBAL: Record<string, { label: string; color: string }> = {
  '2': { label: 'Recusada', color: 'bg-red-500/15 text-red-300 border-red-700' },
  '3': { label: 'Inabilitada', color: 'bg-red-500/15 text-red-300 border-red-700' },
  '5': { label: 'Desclassificada', color: 'bg-red-500/15 text-red-300 border-red-700' },
};

// Desclassificada/inabilitada na Seleção de Fornecedores: portal manda situacao
// 2 (recusada/desistente), 3 (inabilitada) ou 5 (desclassificada), ou preenche
// motivoDesclassificacao. Espelha a lógica do backend (dispute-api.ts).
const isDesclassificadaRow = (r: any): boolean => {
  const s = r?.situacao != null ? String(r.situacao) : '';
  return s === '2' || s === '3' || s === '5'
    || !!(r?.motivoDesclassificacao && String(r.motivoDesclassificacao).trim());
};
const valorDaProposta = (r: any): number | null => {
  const v =
    r?.valores?.valorPropostaInicialOuLances?.valorCalculado?.valorTotal ??
    r?.valores?.valorPropostaInicialOuLances?.valorInformado ??
    r?.valores?.valorPropostaInicial?.valorCalculado?.valorTotal ??
    r?.valores?.valorPropostaInicial?.valorInformado;
  return typeof v === 'number' ? v : null;
};

const ME_EPP_CNPJ_IPCOM = '06163889000104';

// Cada entry pode vir como {items:[...]} OU como array direto [{numero, propostasItem:[...]}]
// (a captura via Firefox grava o array direto). Desembrulha pros participantes.
const getRows = (entry: any): any[] => {
  if (!entry) return [];
  const arr: any[] = Array.isArray(entry) ? entry : Array.isArray(entry.items) ? entry.items : [];
  if (arr.length && Array.isArray(arr[0]?.propostasItem)) return arr[0].propostasItem;
  return arr;
};
// Wrapper {numero, descricao} (metadados do item) — pro título por item.
const getWrapper = (entry: any): any => {
  if (!entry) return null;
  const arr: any[] = Array.isArray(entry) ? entry : Array.isArray(entry.items) ? entry.items : [];
  return arr[0] || null;
};

/** Tabela de ranking de UM item (ou do grupo agregado). */
function RankingTable({ rawRows }: { rawRows: any[] }) {
  // O portal NÃO envia `classificacao` na Seleção de Fornecedores (vem null pra
  // todos). Quando isso acontece, calcula o rank 1..N só entre as VÁLIDAS (não
  // desclassificadas), ordenadas por valor asc — assim o vencedor real é a 1ª
  // proposta válida mesmo que existam lances menores já desclassificados.
  const computedCls = new Map<any, number>();
  if (!rawRows.some((r) => r?.classificacao != null)) {
    rawRows
      .filter((r) => !isDesclassificadaRow(r))
      .filter((r) => { const v = valorDaProposta(r); return v != null && v > 0; })
      .sort((a, b) => (valorDaProposta(a) as number) - (valorDaProposta(b) as number))
      .forEach((r, idx) => computedCls.set(r, idx + 1));
  }
  const clsOf = (r: any): number | null =>
    r?.classificacao != null ? r.classificacao : (computedCls.get(r) ?? null);
  const rows = [...rawRows].sort((a: any, b: any) => {
    const ca = clsOf(a), cb = clsOf(b);
    if (ca == null && cb == null) return 0;
    if (ca == null) return 1;
    if (cb == null) return -1;
    return ca - cb;
  });

  return (
    <div className="bg-central-elevado border border-central-borda rounded-lg overflow-hidden">
      <table className="w-full text-sm table-fixed">
        <thead>
          <tr className="text-left text-xs uppercase text-slate-500 border-b border-central-borda bg-central-cartao">
            <th className="px-3 py-2 text-center w-12">#</th>
            <th className="px-3 py-2">Fornecedor</th>
            <th className="px-3 py-2 text-center w-14">UF</th>
            <th className="px-3 py-2 text-center w-16">ME/EPP</th>
            <th className="px-3 py-2 text-right w-32">Valor inicial</th>
            <th className="px-3 py-2 text-right w-32">Valor final</th>
            <th className="px-3 py-2 text-right w-20">Desconto</th>
            <th className="px-3 py-2 text-center w-36">Situação</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any, i: number) => {
            const cls = clsOf(r);
            const cnpj = r.participante?.identificacao;
            const nome = r.participante?.nome;
            const uf = r.participante?.endereco?.uf;
            const valIni = r.valores?.valorPropostaInicial?.valorCalculado?.valorTotal ?? r.valores?.valorPropostaInicial?.valorInformado;
            const valFin = r.valores?.valorPropostaInicialOuLances?.valorCalculado?.valorTotal ?? r.valores?.valorPropostaInicialOuLances?.valorInformado ?? valIni;
            const desconto = valIni && valFin && valIni > 0 ? ((valIni - valFin) / valIni) * 100 : null;
            const isMine = cnpj === ME_EPP_CNPJ_IPCOM;
            const sit = r.situacaoUltimaNegociacao;
            const sitBadge = sit ? SITUACAO_BADGES[sit] : null;
            const situacaoGlobal = r.situacao != null ? SITUACAO_GLOBAL[String(r.situacao)] : null;
            const isInabilitada = cls == null;
            return (
              <tr key={i} className={`border-b border-slate-800/70 last:border-0 ${isInabilitada ? 'opacity-60 bg-red-500/5' : isMine ? 'bg-emerald-500/10' : i % 2 === 0 ? 'bg-central-elevado' : ''}`}>
                <td className="px-3 py-2.5 text-center">
                  {isInabilitada ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold bg-red-500/15 text-red-400 ring-1 ring-red-700" title="Inabilitada/Desclassificada">
                      ×
                    </span>
                  ) : (
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                      cls === 1 ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500' :
                      cls === 2 ? 'bg-slate-400/20 text-slate-300' :
                      cls === 3 ? 'bg-orange-700/30 text-orange-300' :
                      isMine ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500' :
                      'bg-slate-700 text-slate-400'
                    }`}>
                      {cls}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className={`text-sm ${isMine ? 'text-emerald-300 font-bold' : 'text-slate-200'}`}>
                    {isMine && <span className="inline-block mr-1 text-emerald-500">▸</span>}
                    {nome || '—'}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">{cnpj || '—'}</div>
                </td>
                <td className="px-3 py-2.5 text-center text-xs text-slate-400 font-medium">{uf || '—'}</td>
                <td className="px-3 py-2.5 text-center">
                  {r.declaracaoMeEpp ? (
                    <span className="text-emerald-400 text-base">✓</span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-slate-500 font-mono text-xs">{fmtBRL4(valIni)}</td>
                <td className={`px-3 py-2.5 text-right font-mono font-medium ${isMine ? 'text-emerald-300' : 'text-slate-100'}`}>{fmtBRL4(valFin)}</td>
                <td className="px-3 py-2.5 text-right">
                  {desconto !== null && (
                    <span className={`text-xs font-mono ${desconto > 50 ? 'text-amber-400' : desconto > 20 ? 'text-blue-400' : 'text-slate-400'}`}>
                      −{desconto.toFixed(1)}%
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {sitBadge && (
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${sitBadge.color}`}>
                      {sit === 'E' && <MessageSquare size={10} />}
                      {sitBadge.label}
                    </span>
                  )}
                  {situacaoGlobal && (
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${situacaoGlobal.color}`}>
                      {situacaoGlobal.label}
                    </span>
                  )}
                  {(r.motivoDesclassificacao || r.justificativaUltimaNegociacao) && (
                    <div className="text-xs text-slate-500 mt-1 italic truncate max-w-[200px]" title={r.motivoDesclassificacao || r.justificativaUltimaNegociacao}>
                      “{(r.motivoDesclassificacao || r.justificativaUltimaNegociacao).trim().substring(0, 60)}…”
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ehIpcom = (nome: string) => /IPCOM\s+TECNOLOGIA/i.test(String(nome || ''));

/** Uma linha de ranking no shape do BLL/BNC (name, totalValue, position). */
function LinhaLote({ r, destaque }: { r: any; destaque: boolean }) {
  return (
    <tr className={destaque ? 'bg-emerald-500/5' : ''}>
      <td className="py-2 pl-3 pr-2 w-10">
        <span
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold ${
            r.position === 1
              ? 'bg-amber-500/20 text-amber-300 border border-amber-600/40'
              : 'bg-slate-700/60 text-slate-400'
          }`}
        >
          {r.position}
        </span>
      </td>
      <td className="py-2 pr-2">
        <span className={destaque ? 'text-emerald-300 font-semibold' : 'text-slate-300'}>{r.name}</span>
      </td>
      <td className="py-2 pr-3 text-right font-mono text-slate-200">{fmtBRL4(r.totalValue ?? r.total)}</td>
    </tr>
  );
}

/**
 * Ranking de BLL/BNC com vários lotes: uma tabela POR LOTE, mais a classificação
 * global quando o edital foi marcado como julgado pela soma.
 *
 * Existe porque no PE 021/2026 a tela mostrava um ranking só, do lote 1, sob o
 * rótulo "ranking pelo total do grupo" — e a IPCOM aparecia em 1º quando tinha
 * ganho apenas aquele lote e ficado em 2º na licitação.
 */
function RankingPorLote({ lotes, global, session }: { lotes: any[]; global: any[]; session: any }) {
  const minhaGlobal = global.find((g) => ehIpcom(g.name));
  const qtdDoLote = (num: any): number =>
    (session?.itemLimits || [])
      .filter((il: any) => Number(il.groupNumber) === Number(num))
      .reduce((acc: number, il: any) => acc + (Number(il.quantity) || 0), 0);

  return (
    <div className="px-4 py-4 space-y-6">
      {/* A classificação que vale, quando o edital julga pela soma. Vem primeiro
          porque nesse caso o placar por lote é meio do caminho, não resultado. */}
      {global.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-slate-300 font-semibold">
              Classificação GLOBAL <span className="font-normal text-slate-500">— soma dos {lotes.length} lotes</span>
            </div>
            {minhaGlobal && (
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  minhaGlobal.position === 1
                    ? 'bg-emerald-500/15 text-emerald-300'
                    : 'bg-orange-500/15 text-orange-300'
                }`}
              >
                IPCOM em {minhaGlobal.position}º de {global.length}
              </span>
            )}
          </div>
          <div className="rounded-lg border border-central-borda overflow-hidden">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-central-borda">
                {global.map((g) => (
                  <LinhaLote key={`gl-${g.position}`} r={{ ...g, totalValue: g.total }} destaque={ehIpcom(g.name)} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            Soma do lance de cada fornecedor nos lotes (valor × quantidade). É a conta que o portal não faz — ele mostra um 1º colocado por lote.
          </p>
        </div>
      )}

      {lotes.map((l) => {
        const minha = (l.competitors || []).find((c: any) => ehIpcom(c.name));
        const qtd = qtdDoLote(l.lote);
        return (
          <div key={`lote-${l.lote}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-slate-300 font-semibold">
                Lote {l.lote}
                {qtd > 0 && <span className="font-normal text-slate-500"> — {qtd} un</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  <strong className="text-slate-300">{l.total}</strong> {l.total === 1 ? 'fornecedor' : 'fornecedores'}
                </span>
                {minha && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      minha.position === 1
                        ? 'bg-emerald-500/15 text-emerald-300'
                        : 'bg-orange-500/15 text-orange-300'
                    }`}
                  >
                    IPCOM {minha.position}º
                  </span>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-central-borda overflow-hidden">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-central-borda">
                  {(l.competitors || []).map((c: any) => (
                    <LinhaLote key={`l${l.lote}-${c.position}`} r={c} destaque={ehIpcom(c.name)} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Ranking da Seleção de Fornecedores.
 *  - GRUPO: ranking único agregado em `todasPropostas['-1']` (julgado pelo total).
 *  - POR ITEM (dispensa/pregão item): UM ranking por item (cada item é julgado
 *    isolado, com concorrentes/vencedor próprios). Antes mostrava só o 1º item
 *    (incidente DE 62/2026: item 2, onde a IPCOM era 1º, sumia). */
export function DisputeRankingInline({ session }: Props) {
  const tp: Record<string, any> = session?.rankingData?.todasPropostas || {};
  const capturedAt = session?.rankingData?.capturedAt || null;

  // BLL/BNC multi-lote: cada lote é uma competição separada, com concorrentes e
  // vencedor próprios. O caminho abaixo (todasPropostas do ComprasNet) mostrava
  // UMA tabela rotulada "ranking pelo total do grupo" — que na verdade era o
  // ranking de UM lote apresentado como se fosse o de todos.
  // `resultData.lotes` vem do collectResult, que busca o participantRanking de
  // cada linha do fragmento e casa pelo NÚMERO do lote.
  const lotesBll: any[] = Array.isArray(session?.resultData?.lotes) ? session.resultData.lotes : [];
  const globalBll: any[] = Array.isArray(session?.resultData?.classificacaoGlobal)
    ? session.resultData.classificacaoGlobal
    : [];
  if (lotesBll.length > 0) {
    return <RankingPorLote lotes={lotesBll} global={globalBll} session={session} />;
  }

  // GRUPO: detecta a entry de grupo pelo CONTEÚDO (wrapper tipo 'G' ou numero -1),
  // NÃO só pela chave '-1'. A captura grava o grupo ora sob '-1', ora sob o número
  // do grupo (ex '1' — wrapper {tipo:'G', numero:-1, identificador:'G1'}). Antes só
  // olhava '-1' E a chave '1' caía no filtro de "membro de grupo" → o ranking sumia
  // mesmo coletado (PE 90003/2026 São Gabriel: grupo gravado sob a chave '1').
  const isGroupEntry = (k: string): boolean => {
    if (k === '-1') return true;
    const w = getWrapper(tp[k]);
    return w?.tipo === 'G' || w?.numero === -1;
  };
  const groupKeysRaw = Object.keys(tp).filter((k) => isGroupEntry(k) && getRows(tp[k]).length > 0);
  // DEDUP (08/07, PE 90004 Fazenda Rio Grande): a captura replica o MESMO ranking
  // de grupo sob VÁRIAS chaves (-1 e cada item 1..N), às vezes com metadata
  // contraditória (mesmo grupo rotulado "Grupo 1" numas chaves e "Grupo 3" na -1
  // quando era o Grupo 2). Isso renderizava o mesmo ranking 6×. Colapsa por
  // ASSINATURA das linhas (CNPJ+valor) → uma tabela por ranking distinto.
  const groupSig = (rows: any[]): string =>
    rows
      .map((r) => `${r?.participante?.identificacao || ''}:${valorDaProposta(r) ?? ''}`)
      .sort()
      .join('|');
  const seenSigs = new Set<string>();
  const groupKeys = groupKeysRaw
    .slice()
    .sort((a, b) => (a === '-1' ? -1 : b === '-1' ? 1 : 0)) // '-1' primeiro (canônico)
    .filter((k) => {
      const sig = groupSig(getRows(tp[k]));
      if (seenSigs.has(sig)) return false;
      seenSigs.add(sig);
      return true;
    });
  const isGrupo = groupKeys.length > 0;

  // POR ITEM (avulsos): chaves que NÃO são grupo e NÃO são membros de grupo. No
  // GRUPO+ITEM MISTO, o grupo é mostrado E os itens avulsos têm ranking próprio
  // (cada um julgado isolado). Filtra os MEMBROS de grupo — compartilham o ranking
  // do grupo e re-aparecem na captura passiva como chaves de item.
  const groupMemberNums = new Set(
    (session?.itemLimits || [])
      .filter((x: any) => x.groupNumber != null || x.groupIdentificador != null)
      .map((x: any) => String(x.itemNumber)),
  );
  const itemKeys = Object.keys(tp)
    .filter((k) => !isGroupEntry(k) && getRows(tp[k]).length > 0 && !groupMemberNums.has(k))
    .sort((a, b) => Number(a) - Number(b));

  const temAlgum = isGrupo || itemKeys.length > 0;
  if (!temAlgum) {
    return (
      <div className="px-6 py-12 text-center">
        <Award size={36} className="mx-auto mb-3 text-slate-600" />
        <div className="text-slate-400 text-sm">Ranking ainda não foi coletado.</div>
        <div className="text-xs text-slate-500 mt-1">
          Disponível durante e após a disputa, quando a Seleção de Fornecedores for publicada no portal (botão <strong>Coletar resultado</strong>).
        </div>
      </div>
    );
  }

  // Descrição do item: do wrapper do scrape (numero/descricao) ou do itemLimits.
  const descItem = (key: string): string => {
    const w = getWrapper(tp[key]);
    const lim = (session?.itemLimits || []).find((x: any) => String(x.itemNumber) === key);
    const desc = w?.descricao || lim?.descricao || lim?.itemName || lim?.descricaoItem || '';
    const num = w?.numero ?? key;
    const d = String(desc).trim();
    return d ? `Item ${num} — ${d.length > 70 ? d.slice(0, 70) + '…' : d}` : `Item ${num}`;
  };

  return (
    <div className="px-4 py-4 space-y-5">
      {capturedAt && (
        <div className="text-xs text-slate-500 text-right">
          Atualizado às {new Date(capturedAt).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
        </div>
      )}

      {/* GRUPO + ITEM MISTO: mostra o ranking do grupo (-1) E os itens avulsos,
          cada um separado (cada competição é julgada isolada). */}
      {groupKeys.map((gk) => {
        const grupoRows = getRows(tp[gk]);
        // Rótulo: só mostra número/identificador do grupo se houver MAIS de um
        // grupo distinto (multi-grupo real). Com um grupo só, o número capturado
        // é não-confiável (metadata contraditória — vide dedup acima) → "Grupo".
        const w = getWrapper(tp[gk]);
        const label = groupKeys.length > 1
          ? (w?.descricao || (w?.identificador ? `Grupo ${w.identificador}` : 'Grupo'))
          : 'Grupo';
        return (
          <div key={`g-${gk}`}>
            <div className="text-sm text-slate-400 mb-2">
              <strong className="text-slate-200">{grupoRows.length}</strong> fornecedores participaram — {label} (ranking pelo total do grupo)
            </div>
            <RankingTable rawRows={grupoRows} />
          </div>
        );
      })}
      {itemKeys.map((key) => {
        const rows = getRows(tp[key]);
        return (
          <div key={key}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-200">{descItem(key)}</div>
              <div className="text-xs text-slate-500">
                <strong className="text-slate-300">{rows.length}</strong> {rows.length === 1 ? 'fornecedor' : 'fornecedores'}
              </div>
            </div>
            <RankingTable rawRows={rows} />
          </div>
        );
      })}
    </div>
  );
}
